/**
 * app-search — types + validator for the global search-state of `_app` layout.
 *
 * Centralizes in the URL all overlays mounted at the `_app` level (command
 * palette and global dialogs of `AppLayoutDialogs`), so that pasting the
 * URL in another browser reopens the exact same screen/dialog.
 *
 * Pattern (same as #9 `/?q=`): `validateSearch` omits defaults (so it does not
 * pollute the URL in empty state) and `AppLayoutContent` reflects URL→signals + navigates with
 * `{ replace: true }` on open/close (idempotent, survives back/forward and
 * a pasted URL).
 */

/** Identity of open global dialog (one at a time). */
type AppDialogKind = "choice" | "list-new" | "list-edit" | "group-new" | "group-edit" | "share";

/** Modal with no entity (shortcut reference). */
type AppModalKind = "shortcuts";

interface AppLayoutSearch {
  // ── Command palette ────────────────────────────────────────────────────────
  /** Palette open. */
  palette?: true;
  /** Query typed into palette (copyable). */
  pq?: string;
  /** Palette filter (all = default omitted). */
  pf?: "todo" | "list";

  // ── Global dialogs (AppLayoutDialogs) ────────────────────────────────────
  /** Open global dialog. */
  dialog?: AppDialogKind;
  /** List id for `list-edit` / `share`. */
  listId?: string;
  /** Group id for `group-edit`. */
  groupId?: string;
  /** Open modal with no entity. */
  modal?: AppModalKind;

  // ── Task detail (drawer) — sub-states ─────────────────────────────────
  /** Schedule panel (date/reminder/recurrence) open in detail. */
  sched?: true;

  // ── Task list view — view state ──────────────────────────────
  /** "Completed" section expanded (default collapsed → omitted). */
  done?: true;

  // ── Account switch (multi-account #18) ─────────────────────────────────────
  /** Account switcher open (`?account=switch`). */
  account?: "switch";
}

const DIALOG_KINDS = new Set<AppDialogKind>([
  "choice",
  "list-new",
  "list-edit",
  "group-new",
  "group-edit",
  "share",
]);

function str(v: unknown): string | undefined {
  return typeof v === "string" && v.trim() !== "" ? v : undefined;
}

// Type-guard: narrows a string to AppDialogKind without unsafe assertion. The cast to
// ReadonlySet<string> only WIDENS the Set type (safe), does not narrow value.
function isDialogKind(v: string): v is AppDialogKind {
  return (DIALOG_KINDS as ReadonlySet<string>).has(v);
}

/**
 * Validates + normalizes the layout search. Omits defaults to keep the URL
 * clean. Tolerant: unknown values are discarded (rendering does not break).
 */
export function validateAppLayoutSearch(search: Record<string, unknown>): AppLayoutSearch {
  const out: AppLayoutSearch = {};

  // ── Palette ──
  if (search.palette === true || search.palette === "1" || search.palette === "true") {
    out.palette = true;
  }
  const pq = str(search.pq);
  if (pq) {
    out.pq = pq;
    // A query implies open palette even if flag is missing (shorter URL).
    out.palette = true;
  }
  if (search.pf === "todo" || search.pf === "list") {
    out.pf = search.pf;
    out.palette = true;
  }

  // ── Global dialogs ──
  const dialog = str(search.dialog);
  if (dialog && isDialogKind(dialog)) {
    out.dialog = dialog;
    const listId = str(search.listId);
    const groupId = str(search.groupId);
    if (listId) {
      out.listId = listId;
    }
    if (groupId) {
      out.groupId = groupId;
    }
  }

  // ── Modal with no entity ──
  if (search.modal === "shortcuts") {
    out.modal = "shortcuts";
  }

  // ── Detail: schedule panel ──
  if (search.sched === true || search.sched === "1" || search.sched === "true") {
    out.sched = true;
  }

  // ── List view: completed expanded ──
  if (search.done === true || search.done === "1" || search.done === "true") {
    out.done = true;
  }

  // ── Account switcher (multi-account) ──
  if (search.account === "switch") {
    out.account = "switch";
  }

  return out;
}
